import { useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { usePublicProfile } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { Loader, ErrorView } from '../components/StateViews';
import { Avatar } from '../components/Avatar';
import { BackIcon } from '../components/icons';
import { ShowcaseCard } from '../components/achievements/ShowcaseCard';
import { backButton } from '../telegram';
import { shortDate, seasonLabel } from '../format';

/** Read-only public profile of another player (stats + recent tournaments). */
export function PlayerProfileScreen() {
  const { id } = useParams();
  const pid = Number(id);
  const navigate = useNavigate();
  const { player } = useAuth();
  const { data, isLoading, isError, error } = usePublicProfile(pid);

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  // Own profile reached via a tournament → show the full settings-enabled profile.
  if (player && player.id === pid) return <Navigate to="/profile" replace />;

  if (isLoading) return <Loader />;
  if (isError || !data) return <ErrorView message={(error as Error)?.message} />;

  const { rank, points, wins, gamesPlayed, itmPercent } = data.stats;

  return (
    <div>
      <div className="subhead">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
          <BackIcon />
        </button>
        <div className="title">Профиль игрока</div>
      </div>

      <div className="profile-head">
        <div className="profile-avatar-wrap">
          <Avatar name={data.displayName} src={data.photoUrl} size={92} bordered />
          {rank ? <div className="rank-badge">#{rank}</div> : null}
        </div>
        <div className="profile-name">{data.displayName}</div>
        <div className="profile-sub">
          {points} очков{data.seasonId ? ` · ${seasonLabel(data.seasonId)}` : ''}
        </div>
      </div>

      <ShowcaseCard pid={pid} />

      <div className="stats-grid">
        <Link className="stat-tile stat-tile--profile" to={`/players/${pid}/tournaments?filter=wins`}>
          <div className="val">{wins}×</div>
          <div className="lbl">Победы</div>
        </Link>
        <Link className="stat-tile stat-tile--profile" to={`/players/${pid}/tournaments?filter=inmoney`}>
          <div className="val">{itmPercent}%</div>
          <div className="lbl">В очках</div>
        </Link>
        <Link className="stat-tile stat-tile--profile" to={`/players/${pid}/tournaments`}>
          <div className="val">{gamesPlayed}</div>
          <div className="lbl">Турниров</div>
        </Link>
      </div>

      <div className="section-header">
        <h2>Последние 10 турниров</h2>
        <Link to={`/players/${pid}/tournaments`} className="section-link">
          Все →
        </Link>
      </div>
      {data.recent.length === 0 ? (
        <div className="muted tiny">Нет сыгранных турниров</div>
      ) : (
        <div>
          <div className="lb-head">
            <span>Место · Турнир</span>
            <span>Очки</span>
          </div>
          {data.recent.map((r) => {
            const top = r.position !== null && r.position <= 3;
            return (
              <Link className="recent-row" key={r.gameId} to={`/tournaments/${r.gameId}/results`}>
                <div className={`pos-badge ${top ? 'pos-badge--top' : 'pos-badge--low'}`}>
                  {r.position ?? '—'}
                </div>
                <div className="info">
                  <div className="t">{r.title ?? r.format ?? 'Турнир'}</div>
                  <div className="d">{shortDate(r.startTime)}</div>
                </div>
                {r.points > 0 ? (
                  <div className="pts">+{r.points}</div>
                ) : (
                  <div className="pts pts--none">без очков</div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
